/*
 * Logging settings (log level, console logging) live in localStorage. A reset must never change how
 * logging behaves, because the logs are needed afterwards to find out what caused the problem:
 * - Browser reset (app-reset.ts) keeps these keys while clearing localStorage.
 * - Desktop factory reset wipes the whole webview profile, so a copy is kept on the Rust side
 *   (save_log_settings / load_log_settings) and restored on startup by syncDesktopLogSettings().
 *
 * No static package imports: main.ts uses this before Native Federation and Angular are initialized.
 */

export const LOG_LEVEL_KEY = 'mitel.loglevel';
export const CONSOLE_LOGGING_KEY = 'consoleLoggingEnabled';

const LOG_SETTINGS_KEYS = [LOG_LEVEL_KEY, CONSOLE_LOGGING_KEY];
const PREFIX_LOG = '[LogSettings]';

export type LogSettings = Record<string, string>;

interface TauriInternals {
  invoke: <T>(cmd: string, args?: Record<string, unknown>) => Promise<T>;
}

const getTauriInternals = (): TauriInternals | null =>
  (typeof window !== 'undefined' && (window as unknown as { __TAURI_INTERNALS__?: TauriInternals }).__TAURI_INTERNALS__) || null;

export function readLogSettings(): LogSettings {
  const settings: LogSettings = {};

  LOG_SETTINGS_KEYS.forEach(key => {
    const value = localStorage.getItem(key);

    if (value !== null) {
      settings[key] = value;
    }
  });
  return settings;
}

export function writeLogSettings(settings: LogSettings): void {
  Object.entries(settings)
    .filter(([key]) => LOG_SETTINGS_KEYS.includes(key))
    .forEach(([key, value]) => localStorage.setItem(key, value));
}

/**
 * Desktop only: stores a copy of the current logging settings outside the webview profile.
 */
export async function persistDesktopLogSettings(): Promise<void> {
  const tauri = getTauriInternals();

  if (!tauri) {
    return;
  }

  await tauri.invoke('save_log_settings', { settings: JSON.stringify(readLogSettings()) });
}

/**
 * Desktop only, called from main.ts before the logger is loaded. If the webview profile was wiped
 * (factory reset), the saved logging settings are restored into localStorage. Otherwise the current
 * settings are saved, so a later reset can restore them.
 */
export async function syncDesktopLogSettings(): Promise<void> {
  const tauri = getTauriInternals();

  if (!tauri) {
    return;
  }

  try {
    if (Object.keys(readLogSettings()).length > 0) {
      await persistDesktopLogSettings();
      return;
    }

    const saved = await tauri.invoke<string | null>('load_log_settings');

    if (saved) {
      writeLogSettings(JSON.parse(saved) as LogSettings);
      console.log(`${PREFIX_LOG} Restored logging settings after reset`);
    }
  } catch (error) {
    console.warn(`${PREFIX_LOG} Failed to sync logging settings`, error);
  }
}
